import { COMPARE_OFF, DEFAULT_RANGE_KEY, RANGE_KEYS, RANGE_PARAM, RANGE_PRESET_DAYS, REPORT_MAX_DAYS, type RangeKey } from "./report-constants";

const DAY_MS = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** What the URL holds: the preset, the two dates of a custom range and whether the previous period is compared. */
export interface RangeInput {
  key: RangeKey;
  from: string;
  to: string;
  compare: boolean;
}

/** A period ready to ask the API for: inclusive calendar days in the workspace's time zone. */
export interface ReportRange {
  key: RangeKey;
  from: string;
  to: string;
  days: number;
  compare: boolean;
}

export type RangeProblem = "order" | "long" | null;

export interface ResolvedRange {
  range: ReportRange;
  problem: RangeProblem;
}

const isRangeKey = (value: string | null): value is RangeKey => RANGE_KEYS.some((key) => key === value);
const isIsoDate = (value: string | null): value is string => value !== null && ISO_DATE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

/** `YYYY-MM-DD` of `now` on the calendar of `zone` (the workspace's, not the browser's). */
export function todayIn(zone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function addDays(date: string, delta: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + delta * DAY_MS).toISOString().slice(0, 10);
}

/** Calendar days from `from` to `to`, both included. */
export function daysIn(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
}

export function readRangeInput(params: URLSearchParams): RangeInput {
  const raw = params.get(RANGE_PARAM.range);
  const from = params.get(RANGE_PARAM.from);
  const to = params.get(RANGE_PARAM.to);
  const key = isRangeKey(raw) ? raw : DEFAULT_RANGE_KEY;
  return { key, from: isIsoDate(from) ? from : "", to: isIsoDate(to) ? to : "", compare: params.get(RANGE_PARAM.compare) !== COMPARE_OFF };
}

/** The URL for a range: defaults are left out so a plain `/service-reports` link stays clean. */
export function writeRangeInput(current: URLSearchParams, input: RangeInput): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const name of Object.values(RANGE_PARAM)) next.delete(name);
  if (input.key !== DEFAULT_RANGE_KEY) next.set(RANGE_PARAM.range, input.key);
  if (input.key === "custom") {
    if (input.from) next.set(RANGE_PARAM.from, input.from);
    if (input.to) next.set(RANGE_PARAM.to, input.to);
  }
  if (!input.compare) next.set(RANGE_PARAM.compare, COMPARE_OFF);
  return next;
}

/** Turns the input and today's date into the period to ask for; a bad custom range falls back to the default period and says why. */
export function resolveRange(input: RangeInput, today: string): ResolvedRange {
  const build = (key: RangeKey, from: string, to: string, problem: RangeProblem): ResolvedRange => ({
    range: { key, from, to, days: daysIn(from, to), compare: input.compare },
    problem,
  });
  const thirty = RANGE_PRESET_DAYS["30d"];
  if (input.key === "month") return build("month", `${today.slice(0, 8)}01`, today, null);
  if (input.key !== "custom") return build(input.key, addDays(today, 1 - RANGE_PRESET_DAYS[input.key]), today, null);
  const from = input.from || addDays(today, 1 - thirty);
  const to = input.to || today;
  if (to < from) return build(DEFAULT_RANGE_KEY, addDays(today, 1 - thirty), today, "order");
  if (daysIn(from, to) > REPORT_MAX_DAYS) return build(DEFAULT_RANGE_KEY, addDays(today, 1 - thirty), today, "long");
  return build("custom", from, to, null);
}

/** The period of the same length right before `range` (what "compare" means). */
export function previousRange(range: Pick<ReportRange, "from" | "days">): { from: string; to: string } {
  const to = addDays(range.from, -1);
  return { from: addDays(to, 1 - range.days), to };
}
